import { ReactNode, useEffect, useRef, useState } from 'react';
import { Rnd } from 'react-rnd';

import { Card, IconButton, useMediaQuery, useTheme } from '@mui/material';
import Iconify from '../iconify';

type RndModalProps = {
  open: boolean;
  maximize?: boolean;
  size?: {
    width: number;
    height: number;
  };
  position?: {
    x: number;
    y: number;
  };
  onResizeStop?: ({ width, height }: { width: number; height: number }) => void;
  onDragStop?: ({ x, y }: { x: number; y: number }) => void;
  children?: ReactNode;
};

export const DEFAULT_WIDTH = 350;
export const DEFAULT_HEIGHT = 400;
export const MIN_WIDTH = 200;
export const MIN_HEIGHT = 200;

export const DEFAULT_X = 24;
export const DEFAULT_Y = 24;
export const FULL_SIZE_MARGIN = 24;

export const DEFAULT_PROPS = {
  width: DEFAULT_WIDTH,
  height: DEFAULT_HEIGHT,
  x: DEFAULT_X,
  y: DEFAULT_Y,
};

export default function RndModal({
  children,
  onDragStop,
  onResizeStop,
  size,
  position,
  open,
  maximize,
}: RndModalProps) {
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const ref = useRef<Rnd>(null);
  const [props, setProps] = useState({
    ...DEFAULT_PROPS,
    width: isDownMd ? MIN_WIDTH : DEFAULT_WIDTH,
    height: isDownMd ? MIN_HEIGHT : DEFAULT_HEIGHT,
  });

  useEffect(() => {
    if ((size || position) && open) {
      const sizeToUpdate = size || {};
      const positionToUpdate = position || {};
      setProps((state) => ({
        ...state,
        ...sizeToUpdate,
        ...positionToUpdate,
      }));
    }
  }, [size, position, open]);

  useEffect(() => {
    const handleOrientationChange = () => {
      if (window.matchMedia('(orientation: portrait)').matches) {
        onDragStop && onDragStop({ x: DEFAULT_X, y: DEFAULT_Y });
      }
      if (window.matchMedia('(orientation: landscape)').matches) {
        onDragStop && onDragStop({ x: DEFAULT_X, y: DEFAULT_Y });
      }
    };
    window.addEventListener('resize', handleOrientationChange);

    return () => {
      window.removeEventListener('resize', handleOrientationChange);
    };
  }, [onDragStop]);

  const isMaximizedOnMobile = isDownMd && maximize;

  return open ? (
    <Rnd
      ref={ref}
      bounds="parent"
      disableDragging={isMaximizedOnMobile}
      enableResizing={!isMaximizedOnMobile}
      size={{
        width: props.width,
        height: props.height,
      }}
      position={{ x: props.x, y: props.y }}
      onDragStop={(_e, d) => {
        setProps((currentState) => ({ ...currentState, x: d.x, y: d.y }));
        onDragStop && onDragStop({ x: d.x, y: d.y });
      }}
      onResize={(_e, _dir, elementRef, _delta, position) => {
        setProps({
          width: Number(elementRef.style.width.replace('px', '')),
          height: Number(elementRef.style.height.replace('px', '')),
          ...position,
        });
        onResizeStop &&
          onResizeStop({
            width: Number(elementRef.style.width.replace('px', '')),
            height: Number(elementRef.style.height.replace('px', '')),
          });
      }}
      onResizeStop={(_e, _dir, elementRef, _delta, position) => {
        setProps({
          width: Number(elementRef.style.width.replace('px', '')),
          height: Number(elementRef.style.height.replace('px', '')),
          ...position,
        });
        onResizeStop &&
          onResizeStop({
            width: Number(elementRef.style.width.replace('px', '')),
            height: Number(elementRef.style.height.replace('px', '')),
          });
      }}
      style={{
        zIndex: theme.zIndex.modal,
        position: 'fixed',
        pointerEvents: 'auto',
      }}
      dragHandleClassName="drag-handle"
      resizeHandleStyles={{
        top: { display: 'none' },
        topLeft: { left: 0, top: 0, display: 'none' },
        topRight: { right: 0, top: 0, display: 'none' },
        right: { display: 'none' },
        bottom: { display: 'none' },
        bottomLeft: { left: 0, bottom: 0, display: 'none' },
        bottomRight: {
          right: 15,
          bottom: 15,
          display: isMaximizedOnMobile ? 'none' : undefined,
        },
        left: { display: 'none' },
      }}
      resizeHandleClasses={{
        top: 'resize-handle',
        topLeft: 'resize-handle',
        topRight: 'resize-handle',
        right: 'resize-handle',
        bottom: 'resize-handle',
        bottomLeft: 'resize-handle',
        bottomRight: 'resize-handle',
        left: 'resize-handle',
      }}
      resizeHandleComponent={{
        bottomRight: (
          <IconButton
            size="small"
            className="resize-handle"
            sx={{ width: '30px', height: '30px' }}
          >
            <Iconify icon="mdi:move-resize-variant" color="text.secondary" />
          </IconButton>
        ),
      }}
      minWidth={isDownLg ? MIN_WIDTH : DEFAULT_WIDTH}
      minHeight={isDownLg ? MIN_HEIGHT : DEFAULT_HEIGHT}
      maxHeight={
        document.body.clientHeight - (isDownMd ? 0 : 2 * FULL_SIZE_MARGIN)
      }
      maxWidth={
        document.body.clientWidth - (isDownMd ? 0 : 2 * FULL_SIZE_MARGIN)
      }
    >
      <Card
        sx={{
          position: 'relative',
          height: '100%',
          borderRadius: isDownMd && maximize ? 0 : undefined,
        }}
      >
        {children}
      </Card>
    </Rnd>
  ) : null;
}
